import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Gates the App Settings Save button behind a passing webhook Test for
// System Alerts *_alert_webhook_url settings (Admin::AppSettingsHelper#setting_form_data
// attaches this controller to the whole <form>, not a local wrapper, so it can
// reach the Save button down in the footer). Save is enabled when the current
// input value is blank (clearing the webhook is always allowed) OR when that
// EXACT value has already passed a Test in this session — editing the value
// after a pass re-locks Save until it is re-tested. This is a client-side
// speed bump only: the server re-validates on submit regardless
// (Admin::AppSettingsController#webhook_url_validation_error), so this
// controller can never be the only thing standing between a bad URL and a save.
export default class extends Controller {
  static targets = ["input", "testButton", "saveButton", "result"]
  static values = { url: String, var: String }

  connect() {
    this.testedValue = null
    this.refreshSaveState()
  }

  onInput() {
    this.clearResult()
    this.refreshSaveState()
  }

  async test() {
    const url = this.inputTarget.value.trim()
    if (!url) return

    // Write the trimmed value back so the tested value is byte-identical to
    // what Save will submit — the server's re-validation is whitespace-
    // sensitive, so a padded paste must not pass Test yet fail Save.
    this.inputTarget.value = url

    this.testButtonTarget.disabled = true
    this.showResult("Testing…", "pending")

    try {
      const response = await post(this.urlValue, {
        body: JSON.stringify({ url, var: this.varValue }),
        contentType: "application/json",
        responseKind: "json",
      })
      const data = await response.json

      if (data.ok) {
        this.testedValue = url
        this.showResult(`Success (HTTP ${data.status})`, "success")
      } else {
        this.testedValue = null
        this.showResult(data.error || `Failed (HTTP ${data.status ?? "no response"})`, "danger")
      }
    } catch {
      this.testedValue = null
      this.showResult("Test request failed — please try again", "danger")
    } finally {
      this.testButtonTarget.disabled = false
      this.refreshSaveState()
    }
  }

  refreshSaveState() {
    if (!this.hasSaveButtonTarget) return

    const url = this.inputTarget.value.trim()
    const passed = url === "" || (this.testedValue !== null && this.testedValue === url)
    this.saveButtonTarget.disabled = !passed
  }

  showResult(message, kind) {
    if (!this.hasResultTarget) return

    // textContent (never innerHTML) — `message` can be a server-provided
    // error string, so this must never be interpreted as markup.
    this.resultTarget.textContent = message
    this.resultTarget.classList.remove("gl-text-success", "gl-text-danger", "gl-text-muted")
    this.resultTarget.classList.add(this.#resultClass(kind))
  }

  clearResult() {
    if (!this.hasResultTarget) return

    this.resultTarget.textContent = ""
    this.resultTarget.classList.remove("gl-text-success", "gl-text-danger", "gl-text-muted")
  }

  #resultClass(kind) {
    if (kind === "success") return "gl-text-success"
    if (kind === "danger") return "gl-text-danger"
    return "gl-text-muted"
  }
}
